import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Home, Zap, Users, FolderKanban, Activity, Settings, LogOut, ChevronRight, TrendingUp, Cpu } from 'lucide-react';
import DashboardHome from './DashboardHome';
import MainLayout from './MainLayout';
import GitHubConnect from './GitHubConnect';
import ImportedProjectView from './ImportedProjectView';
import AgentsView from './AgentsView';
import ProjectsView from './ProjectsView';
import MonetizeView from './MonetizeView';
import HQView from './HQView';
import WingmanHQPanel from './WingmanHQPanel';

const API = process.env.REACT_APP_BACKEND_URL || '';

const NAV_ITEMS = [
  { id: 'home', icon: Home, label: 'Home' },
  { id: 'build', icon: Zap, label: 'Build' },
  { id: 'agents', icon: Users, label: 'Agents' },
  { id: 'projects', icon: FolderKanban, label: 'Projects' },
  { id: 'monetize', icon: TrendingUp, label: 'Monetize', accent: '#FFD700' },
  { id: 'hq', icon: Cpu, label: 'HQ', accent: '#FFD700' },
  { id: 'settings', icon: Settings, label: 'Settings' },
];

export default function StudioLayout({ user, setUser }) {
  const [activeView, setActiveView] = useState('home');
  const [onboarded, setOnboarded] = useState(null);
  const [agents, setAgents] = useState([]);
  const [projects, setProjects] = useState([]);
  const [stats, setStats] = useState(null);
  const [activityFeed, setActivityFeed] = useState([]);
  const [activeImportedProject, setActiveImportedProject] = useState(null);
  // Project to load directly into the Build view (chat sidebar) — { project_id, chat_id? }
  const [buildLoadTarget, setBuildLoadTarget] = useState(null);
  // URL to auto-trigger in the Web Intelligence agent
  const [pendingScrapeUrl, setPendingScrapeUrl] = useState(null);
  // Open tabs in the Build top header (persists across view navigation).
  const [openTabs, setOpenTabs] = useState([]);
  const statsInterval = useRef(null);

  // Check onboarding status
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/api/onboarding`, { credentials: 'include' });
        if (r.ok) {
          const data = await r.json();
          setOnboarded(data.onboarded);
          if (data.onboarded) {
            setAgents(data.agents || []);
            setProjects(data.projects || []);
          }
        }
      } catch { setOnboarded(false); }
    })();
  }, []);

  // Load dashboard data
  const loadDashboard = useCallback(async () => {
    try {
      const [sRes, aRes, pRes, fRes] = await Promise.all([
        fetch(`${API}/api/dashboard/stats`, { credentials: 'include' }),
        fetch(`${API}/api/agents`, { credentials: 'include' }),
        fetch(`${API}/api/projects`, { credentials: 'include' }),
        fetch(`${API}/api/activity?limit=20`, { credentials: 'include' }),
      ]);
      if (sRes.ok) setStats(await sRes.json());
      if (aRes.ok) setAgents(await aRes.json());
      if (pRes.ok) setProjects(await pRes.json());
      if (fRes.ok) setActivityFeed(await fRes.json());
    } catch {}
  }, []);

  useEffect(() => {
    if (onboarded) {
      loadDashboard();
      statsInterval.current = setInterval(loadDashboard, 15000);
      return () => clearInterval(statsInterval.current);
    }
  }, [onboarded, loadDashboard]);

  // Onboarding handler
  const handleOnboard = async (mode, selectedAgents, importedProject) => {
    try {
      const r = await fetch(`${API}/api/onboarding`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ mode, agents: selectedAgents }),
      });
      if (r.ok) {
        setOnboarded(true);
        loadDashboard();
        // If imported project, go to Build view with that project loaded
        if (mode === 'import' && importedProject?.project_id) {
          setActiveImportedProject(importedProject);
          setActiveView('build');
        }
      }
    } catch {}
  };

  const openImportedProject = (projectSummary) => {
    setActiveImportedProject(projectSummary);
    setActiveView('build');
  };

  const closeImportedProject = () => {
    setActiveImportedProject(null);
  };

  // Open a project from the Workspace into the Build view.
  // - imported repo → iframe preview (existing flow)
  // - generative build → load specific chat, or last/auto-create one for project_id
  // - synthetic build (chat-as-project) → the project_id IS the chat_id
  const handleOpenInBuild = useCallback(async (project, explicitChatId) => {
    if (!project) return;
    const isImport = !!project.repo_url || project.type === 'static' || project.type === 'framework';
    let chatId = explicitChatId || null;
    const pid = project.project_id;

    // Resolve / create a chat for this project so the right-side chat panel
    // can persist conversations against the import as well as builds.
    if (!chatId && pid && pid.startsWith('chat_')) {
      chatId = pid;
    }
    if (!chatId && pid) {
      try {
        const r = await fetch(`${API}/api/projects/${pid}/details`, { credentials: 'include' });
        if (r.ok) {
          const d = await r.json();
          if (Array.isArray(d.sessions) && d.sessions.length > 0) {
            chatId = d.sessions[0].chat_id;
          }
        }
      } catch {}
    }
    if (!chatId && pid) {
      try {
        const r = await fetch(`${API}/api/chats`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ title: project.full_name || project.name || 'New Chat', project_id: pid }),
        });
        if (r.ok) {
          const c = await r.json();
          chatId = c.chat_id;
        }
      } catch {}
    }

    if (isImport) {
      setActiveImportedProject(project);
    } else {
      setActiveImportedProject(null);
    }
    setBuildLoadTarget({ project_id: pid, chat_id: chatId });
    setActiveView('build');
  }, []);

  // Re-scrape: open the Build view with Web Intelligence agent + URL pre-filled
  const handleRescrape = useCallback((url) => {
    if (!url) return;
    setActiveImportedProject(null);
    setBuildLoadTarget(null);
    setPendingScrapeUrl(url);
    setActiveView('build');
  }, []);

  // Reset Build to a fresh empty session (used when user starts a new project from scratch)
  const handleStartFreshBuild = useCallback(() => {
    setActiveImportedProject(null);
    setBuildLoadTarget(null);
    setPendingScrapeUrl(null);
    setActiveView('build');
  }, []);

  // Triggered by "New project → Import a repository" — open onboarding GitHub step
  const handleOpenImportFlow = useCallback(() => {
    setActiveView('import');
  }, []);

  const handleLogout = async () => {
    await fetch(`${API}/api/auth/logout`, { method: 'POST', credentials: 'include' });
    setUser(null);
  };

  // Loading
  if (onboarded === null) {
    return (
      <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0A0A0F' }}>
        <div style={{ width: 36, height: 36, border: '3px solid #7C3AED', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
      </div>
    );
  }

  // Onboarding
  if (!onboarded) {
    return <OnboardingFlow user={user} onComplete={handleOnboard} />;
  }

  // Studio
  return (
    <div style={{ display: 'flex', height: '100vh', background: '#0A0A0F', overflow: 'hidden' }}>
      {/* Sidebar */}
      <div data-testid="studio-sidebar" style={{
        width: 64, flexShrink: 0,
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        padding: '16px 0', gap: 4,
        background: '#0A0A0F',
        borderRight: '1px solid rgba(255,255,255,0.06)',
      }}>
        <div style={{
          width: 36, height: 36, background: '#7C3AED',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          marginBottom: 20,
        }}>
          <Zap size={18} color="#000" />
        </div>

        {NAV_ITEMS.map(item => {
          const active = activeView === item.id;
          return (
            <button
              key={item.id}
              data-testid={`nav-${item.id}`}
              onClick={() => setActiveView(item.id)}
              style={{
                width: 44, height: 44,
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                gap: 2, border: 'none', borderRadius: 0,
                background: active ? 'rgba(124,58,237,0.12)' : 'transparent',
                color: active ? '#7C3AED' : 'rgba(255,255,255,0.4)',
                cursor: 'pointer', transition: 'all 0.15s',
                fontFamily: 'inherit',
              }}
              onMouseEnter={e => { if (!active) e.currentTarget.style.color = 'rgba(255,255,255,0.7)'; }}
              onMouseLeave={e => { if (!active) e.currentTarget.style.color = 'rgba(255,255,255,0.4)'; }}
            >
              <item.icon size={18} />
              <span style={{ fontSize: 8, fontWeight: 600, letterSpacing: '0.03em' }}>{item.label}</span>
            </button>
          );
        })}

        <div style={{ flex: 1 }} />
        <button onClick={handleLogout} style={{
          width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center',
          border: 'none', background: 'transparent', color: 'rgba(255,255,255,0.3)', cursor: 'pointer',
        }}>
          <LogOut size={16} />
        </button>
      </div>

      {/* Main content */}
      <div
        data-studio-theme={activeView === 'build' ? 'dark' : 'light'}
        style={{ flex: 1, overflow: 'hidden' }}
      >
        {activeView === 'home' && (
          <DashboardHome
            user={user}
            stats={stats}
            agents={agents}
            projects={projects}
            activityFeed={activityFeed}
            onNavigate={setActiveView}
          />
        )}
        {activeView === 'build' && (
          <MainLayout
            user={user}
            setUser={setUser}
            initialChatId={buildLoadTarget?.chat_id || null}
            initialProjectId={buildLoadTarget?.project_id || null}
            initialScrapeUrl={pendingScrapeUrl}
            importedProject={activeImportedProject}
            onCloseImported={closeImportedProject}
            onActivateImported={(project, chatId) => {
              // Re-mount the imported project from a tab click and re-sync the chat.
              setActiveImportedProject(project);
              if (chatId) setBuildLoadTarget({ project_id: project?.project_id, chat_id: chatId });
            }}
            onConsumeInitial={() => { setBuildLoadTarget(null); setPendingScrapeUrl(null); }}
            openTabs={openTabs}
            setOpenTabs={setOpenTabs}
          />
        )}
        {activeView === 'agents' && (
          <AgentsView onRefresh={loadDashboard} />
        )}
        {activeView === 'projects' && (
          <ProjectsView
            onOpenInBuild={handleOpenInBuild}
            onRescrape={handleRescrape}
            onStartFreshBuild={handleStartFreshBuild}
            onOpenImportFlow={handleOpenImportFlow}
          />
        )}
        {activeView === 'monetize' && (
          <MonetizeView />
        )}
        {activeView === 'hq' && (
          <HQView />
        )}
        {activeView === 'activity' && (
          <ActivityView feed={activityFeed} />
        )}
        {activeView === 'settings' && (
          <SettingsView user={user} />
        )}
        {activeView === 'import' && (
          <ImportRepoView
            onCancel={() => setActiveView('projects')}
            onImported={(repo) => {
              // Once imported, route into Build with the project ready
              const proj = {
                id: repo?.project_id,
                project_id: repo?.project_id,
                kind: 'import',
                full_name: repo?.full_name || repo?.name,
                name: repo?.name,
              };
              handleOpenInBuild(proj);
            }}
          />
        )}
      </div>
    </div>
  );
}

// ========== ONBOARDING ==========
function OnboardingFlow({ user, onComplete }) {
  const [step, setStep] = useState(0); // 0=welcome, 1=mode, 2=import/agents, 3=agents
  const [mode, setMode] = useState(null);
  const [selectedAgents, setSelectedAgents] = useState(['borntobealive-ai']);
  const [repoUrl, setRepoUrl] = useState('');
  const [importing, setImporting] = useState(false);
  const [importStatus, setImportStatus] = useState(null); // { status, name, type, ... }
  const [importError, setImportError] = useState('');

  const AGENTS = [
    { id: 'borntobealive-ai', name: 'Aria', role: 'Builder & Designer', free: true, color: '#7C3AED' },
    { id: 'sales-operator', name: 'Marcus', role: 'Sales Operator', free: false, color: '#FFB020' },
    { id: 'content-director', name: 'Iris', role: 'Content Director', free: false, color: '#A855F7' },
    { id: 'market-analyst', name: 'Elena', role: 'Market Analyst', free: false, color: '#3B82F6' },
    { id: 'product-engineer', name: 'Theo', role: 'Product Engineer', free: false, color: '#EF4444' },
    { id: 'growth-operator', name: 'Naomi', role: 'Growth Operator', free: false, color: '#22C55E' },
    { id: 'web-intelligence', name: 'Kai', role: 'Scraping Expert', free: false, color: '#06B6D4' },
  ];

  const SOURCES = [
    { id: 'github', name: 'GitHub', icon: '/', color: '#fff' },
    { id: 'lovable', name: 'Lovable', icon: 'L', color: '#FF6B6B' },
    { id: 'shopify', name: 'Shopify', icon: 'S', color: '#96BF48' },
    { id: 'wordpress', name: 'WordPress', icon: 'W', color: '#21759B' },
    { id: 'base44', name: 'Base44', icon: 'B', color: '#FFB020' },
    { id: 'vercel', name: 'Vercel', icon: 'V', color: '#fff' },
  ];

  const toggleAgent = (id) => {
    if (id === 'borntobealive-ai') return;
    setSelectedAgents(prev => prev.includes(id) ? prev.filter(a => a !== id) : [...prev, id]);
  };

  const handleImport = async () => {
    if (!repoUrl.trim()) return;
    setImporting(true);
    setImportError('');
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL || ''}/api/import/github`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ repo_url: repoUrl.trim() }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || 'Import failed');
      }
      const data = await r.json();
      setImportStatus(data);

      // If framework, poll for build status
      if (data.type === 'framework') {
        const pollBuild = async () => {
          for (let i = 0; i < 30; i++) {
            await new Promise(res => setTimeout(res, 3000));
            const sr = await fetch(`${process.env.REACT_APP_BACKEND_URL || ''}/api/import/${data.project_id}/status`, { credentials: 'include' });
            if (sr.ok) {
              const st = await sr.json();
              setImportStatus(st);
              if (st.status === 'ready' || st.status === 'build_failed' || st.status === 'build_timeout') break;
            }
          }
        };
        pollBuild();
      }
      // Go to agents step
      setTimeout(() => setStep(3), 1500);
    } catch (e) {
      setImportError(e.message);
    } finally {
      setImporting(false);
    }
  };

  const containerStyle = {
    height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: '#0A0A0F', color: '#fff', padding: 20,
    position: 'relative', overflow: 'hidden',
  };

  const glowStyle = {
    position: 'absolute', top: '-30%', right: '-20%',
    width: '70%', height: '70%',
    background: 'radial-gradient(circle, rgba(124,58,237,0.08) 0%, transparent 60%)',
    pointerEvents: 'none',
  };

  const btnPrimary = {
    padding: '14px 40px', background: '#7C3AED', color: '#000', border: 'none',
    fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
    transition: 'all 0.2s', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
  };

  return (
    <div style={containerStyle}>
      <div style={glowStyle} />

      <div style={{ width: '100%', maxWidth: 620, position: 'relative', zIndex: 1 }}>
        {/* Step 0: Welcome */}
        {step === 0 && (
          <div style={{ textAlign: 'center', animation: 'fadeIn 0.6s ease-out' }}>
            <div style={{
              width: 64, height: 64, background: '#7C3AED', margin: '0 auto 24px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Zap size={28} color="#000" />
            </div>
            <h1 style={{ fontSize: 36, fontWeight: 800, marginBottom: 8, letterSpacing: '-0.03em' }}>
              Welcome, {user?.name?.split(' ')[0]}
            </h1>
            <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: 16, marginBottom: 40 }}>
              Your studio is ready. Let's set it up.
            </p>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <button onClick={() => setStep(1)} style={{ ...btnPrimary, display: 'inline-flex' }}
                onMouseEnter={e => e.currentTarget.style.boxShadow = '0 0 30px rgba(124,58,237,0.4)'}
                onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
              >
                Get Started
              </button>
            </div>
          </div>
        )}

        {/* Step 1: Choose mode */}
        {step === 1 && (
          <div style={{ animation: 'fadeIn 0.5s ease-out' }}>
            <h2 style={{ fontSize: 24, fontWeight: 700, marginBottom: 8, textAlign: 'center' }}>How do you want to start?</h2>
            <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: 14, marginBottom: 32, textAlign: 'center' }}>
              Import an existing project or build from scratch
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <button onClick={() => { setMode('import'); setStep(2); }} style={{
                padding: 28, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)',
                color: '#fff', cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit', transition: 'all 0.2s',
              }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = '#7C3AED'; e.currentTarget.style.background = 'rgba(124,58,237,0.06)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'; e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; }}
              >
                <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>Import Project</div>
                <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', lineHeight: 1.5 }}>
                  Connect your GitHub, Lovable, Shopify, WordPress repository
                </div>
                <div style={{ display: 'flex', gap: 6, marginTop: 14 }}>
                  {SOURCES.slice(0, 4).map(s => (
                    <div key={s.id} style={{
                      width: 24, height: 24, borderRadius: '50%', background: 'rgba(255,255,255,0.08)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 10, fontWeight: 800, color: s.color,
                    }}>{s.icon}</div>
                  ))}
                </div>
              </button>
              <button onClick={() => { setMode('build'); setStep(3); }} style={{
                padding: 28, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)',
                color: '#fff', cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit', transition: 'all 0.2s',
              }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = '#7C3AED'; e.currentTarget.style.background = 'rgba(124,58,237,0.06)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'; e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; }}
              >
                <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>Build from Scratch</div>
                <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', lineHeight: 1.5 }}>
                  Use our Analysis &gt; Design &gt; Code pipeline with AI
                </div>
                <div style={{ display: 'flex', gap: 6, marginTop: 14 }}>
                  <div style={{ width: 24, height: 24, background: 'rgba(124,58,237,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Zap size={11} color="#7C3AED" />
                  </div>
                </div>
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Import — Connect GitHub */}
        {step === 2 && (
          <div style={{ animation: 'fadeIn 0.5s ease-out' }}>
            <h2 style={{ fontSize: 24, fontWeight: 700, marginBottom: 8, textAlign: 'center' }}>Connect Your Project</h2>
            <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: 14, marginBottom: 24, textAlign: 'center' }}>
              Connect your GitHub to access all your repositories
            </p>

            {/* Source selector */}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginBottom: 24 }}>
              {SOURCES.map(s => (
                <div key={s.id} style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
                  padding: '10px 14px',
                  background: s.id === 'github' ? 'rgba(124,58,237,0.08)' : 'rgba(255,255,255,0.03)',
                  border: `1px solid ${s.id === 'github' ? 'rgba(124,58,237,0.2)' : 'rgba(255,255,255,0.05)'}`,
                  opacity: s.id === 'github' ? 1 : 0.4,
                  cursor: s.id === 'github' ? 'default' : 'not-allowed',
                }}>
                  <div style={{
                    width: 28, height: 28, borderRadius: '50%', background: 'rgba(255,255,255,0.08)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 11, fontWeight: 800, color: s.color,
                  }}>{s.icon}</div>
                  <span style={{ fontSize: 9, fontWeight: 600, color: 'rgba(255,255,255,0.5)' }}>{s.name}</span>
                  {s.id !== 'github' && <span style={{ fontSize: 7, color: 'rgba(255,255,255,0.2)' }}>SOON</span>}
                </div>
              ))}
            </div>

            <GitHubConnect
              onRepoSelected={(repo) => { setImportStatus(repo); }}
              importStatus={importStatus}
              onNext={() => setStep(3)}
              onBack={() => setStep(1)}
            />
          </div>
        )}

        {/* Step 3: Select agents */}
        {step === 3 && (
          <div style={{ animation: 'fadeIn 0.5s ease-out' }}>
            <h2 style={{ fontSize: 24, fontWeight: 700, marginBottom: 8, textAlign: 'center' }}>Connect Your Agents</h2>
            <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: 14, marginBottom: 24, textAlign: 'center' }}>
              Choose who works for you 24/7
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              {AGENTS.map(agent => {
                const selected = selectedAgents.includes(agent.id);
                return (
                  <button key={agent.id} onClick={() => toggleAgent(agent.id)} style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    padding: '14px 16px', textAlign: 'left',
                    background: selected ? `${agent.color}12` : 'rgba(255,255,255,0.03)',
                    border: `1px solid ${selected ? agent.color + '40' : 'rgba(255,255,255,0.06)'}`,
                    color: '#fff', cursor: agent.free ? 'default' : 'pointer',
                    fontFamily: 'inherit', transition: 'all 0.15s',
                  }}>
                    <div style={{
                      width: 32, height: 32, borderRadius: '50%',
                      background: agent.color + '20',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 12, fontWeight: 800, color: agent.color,
                    }}>
                      {agent.name[0]}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600 }}>{agent.name}</div>
                      <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)' }}>{agent.role}</div>
                    </div>
                    {agent.free ? (
                      <span style={{ fontSize: 9, fontWeight: 700, color: '#7C3AED', background: 'rgba(124,58,237,0.15)', padding: '2px 6px' }}>FREE</span>
                    ) : (
                      <span style={{ fontSize: 9, fontWeight: 600, color: 'rgba(255,255,255,0.3)' }}>PRO</span>
                    )}
                  </button>
                );
              })}
            </div>
            <button onClick={() => onComplete(mode, selectedAgents, importStatus)} style={{
              ...btnPrimary, width: '100%', marginTop: 20,
            }}>
              Launch Studio <ChevronRight size={16} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ========== SIMPLE VIEWS (Activity, Settings) ==========
function ActivityView({ feed }) {
  return (
    <div style={{ padding: 32, overflowY: 'auto', height: '100%', color: '#0F0F1A' }}>
      <h2 style={{ fontSize: 22, fontWeight: 700, marginBottom: 20, letterSpacing: '-0.01em' }}>Activity Feed</h2>
      {feed.map((ev, i) => (
        <div key={i} style={{ display: 'flex', gap: 12, padding: '12px 0', borderBottom: '1px solid rgba(15,15,30,0.06)' }}>
          <div style={{ width: 32, height: 32, borderRadius: '50%', background: (ev.color || '#7C3AED') + '20', display: 'flex', alignItems: 'center', justifyContent: 'center', color: ev.color || '#7C3AED', fontSize: 11, fontWeight: 700, flexShrink: 0 }}>{ev.agent?.[0] || 'S'}</div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 500, color: '#0F0F1A' }}>{ev.message}</div>
            <div style={{ fontSize: 10, color: 'rgba(15,15,30,0.45)', marginTop: 2 }}>{ev.agent} - {ev.time_ago || 'just now'}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

function SettingsView({ user }) {
  return (
    <div style={{ flex: 1, height: '100%', overflowY: 'auto', background: 'linear-gradient(180deg, #FBFAF7 0%, #F4F3F0 100%)' }}>
      <div style={{ padding: 32, color: '#0F0F1A', maxWidth: 1200, margin: '0 auto' }}>
        <h2 style={{ fontSize: 22, fontWeight: 700, marginBottom: 20, letterSpacing: '-0.01em' }}>Settings</h2>
        <div style={{ padding: 20, background: '#FFFFFF', border: '1px solid rgba(15,15,30,0.08)', borderRadius: 12, maxWidth: 400, boxShadow: '0 1px 3px rgba(15,15,30,0.04)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
            {user?.picture && <img src={user.picture} alt="" style={{ width: 40, height: 40, borderRadius: '50%' }} />}
            <div>
              <div style={{ fontWeight: 600 }}>{user?.name}</div>
              <div style={{ fontSize: 12, color: 'rgba(15,15,30,0.5)' }}>{user?.email}</div>
            </div>
          </div>
          <div style={{ fontSize: 12, color: 'rgba(15,15,30,0.45)' }}>Plan: Free</div>
        </div>

        <div style={{ marginTop: 28 }}>
          <div style={{
            fontSize: 10.5, fontWeight: 800, letterSpacing: '0.16em',
            textTransform: 'uppercase', color: 'rgba(15,15,30,0.5)',
            marginBottom: 8, paddingLeft: 2,
          }}>
            External integration
          </div>
          <div style={{
            fontSize: 12, color: 'rgba(15,15,30,0.55)',
            marginBottom: 14, paddingLeft: 2, lineHeight: 1.55, maxWidth: 640,
          }}>
            Live read-only feed from the external Wingman HQ tracker. Surfaced
            here for visibility — your venture's own Mission Control lives in HQ.
          </div>
        </div>
        <WingmanHQPanel />
      </div>
    </div>
  );
}


// ========== STANDALONE IMPORT REPO VIEW ==========
function ImportRepoView({ onCancel, onImported }) {
  const [importStatus, setImportStatus] = useState(null);
  return (
    <div style={{
      flex: 1, height: '100%', overflowY: 'auto',
      background: 'linear-gradient(180deg, #FBFAF7 0%, #F4F3F0 100%)',
    }}>
      <div style={{ maxWidth: 720, margin: '40px auto 0', padding: '0 32px 60px' }}>
        <button
          onClick={onCancel}
          data-testid="import-repo-cancel"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: 0, marginBottom: 18,
            background: 'transparent', border: 'none',
            color: 'rgba(15,15,30,0.55)', fontSize: 12, cursor: 'pointer',
            fontFamily: 'inherit',
          }}
        >
          ← Back to Workspace
        </button>
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          fontSize: 9, fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase',
          color: '#A855F7', padding: '4px 8px',
          background: 'rgba(168,85,247,0.1)', border: '1px solid rgba(168,85,247,0.3)',
          marginBottom: 14,
        }}>
          Import flow
        </div>
        <h1 style={{ fontSize: 28, fontWeight: 800, margin: '0 0 8px', color: '#0F0F1A', letterSpacing: '-0.015em' }}>
          Import a repository
        </h1>
        <p style={{ fontSize: 13, color: 'rgba(15,15,30,0.6)', lineHeight: 1.6, marginBottom: 28 }}>
          Connect your GitHub, pick a repo, and we'll clone it into the studio. You'll be able to
          run it side-by-side with the AI consultant in Build.
        </p>
        <div style={{
          padding: 26,
          background: '#0F0F1A',
          border: '1px solid rgba(255,255,255,0.06)',
          borderRadius: 16,
          boxShadow: '0 10px 40px rgba(15,15,30,0.18)',
        }}>
          <GitHubConnect
            onRepoSelected={(repo) => setImportStatus(repo)}
            importStatus={importStatus}
            onNext={() => onImported?.(importStatus)}
            onBack={onCancel}
          />
        </div>
      </div>
    </div>
  );
}
